import {
  createProductDetailModel,
  resolveApplicationPortalProductFee,
  transformUserExtraInfoConfig
} from '@repo/payments-core';
import StepConfig from 'libs/fields-manager/src/shared/stores/models/StepConfig';
import { RequestObjectExtraFieldsDataType } from 'libs/fields-manager/src/shared/types/zodSchemaTypes';

class ApplicationSectionProductModel {
  enrollmentInstallmentMeta:
    | {
        installmentType:
          | 'CUSTOMIZABLE'
          | 'EMI'
          | 'PERIODIC'
          | 'EXTERNAL_MANAGED';
        minimumAmountToBePaid: number;
        totalAmountToBePaid: number;
        defaultAmountToBePaid: number;
        appliedNoOfMonths?: number;
        appliedConfigId?: string;
        externalServiceType?: 'INSTITUTE';
        externalServiceReferenceId?: string;
      }
    | null = null;
  productCode: string;
  productFee: number;
  termsAndConditions: Array<string>;
  productFeeAfterDiscount: number | undefined;
  referralDiscount: number | undefined;
  couponDiscount: number | undefined = 0;

  productFieldDetails?: any;
  productRequestObjectKeys?: RequestObjectExtraFieldsDataType;

  productValidationObject: Record<string, string> = {};
  extraDataConfig?: any;
  successGAEvent?: string;
  applicableDiscounts?: any;

  private updateEnrollmentInstallmentMeta(paymentsProductForStepFields: ReturnType<
    typeof createProductDetailModel
  > | null) {
    const installmentType =
      paymentsProductForStepFields &&
      typeof paymentsProductForStepFields === 'object'
        ? (paymentsProductForStepFields as { installmentType?: string })
            .installmentType
        : undefined;
    if (
      installmentType !== 'CUSTOMIZABLE' &&
      installmentType !== 'EMI' &&
      installmentType !== 'PERIODIC' &&
      installmentType !== 'EXTERNAL_MANAGED'
    ) {
      this.enrollmentInstallmentMeta = null;
      return;
    }

    const externalManagedFields =
      installmentType === 'EXTERNAL_MANAGED'
        ? {
            externalServiceType: (
              paymentsProductForStepFields as {
                externalServiceType?: 'INSTITUTE';
              }
            ).externalServiceType,
            externalServiceReferenceId: (
              paymentsProductForStepFields as {
                externalServiceReferenceId?: string;
              }
            ).externalServiceReferenceId
          }
        : {};

    const pricingDetails = (
      paymentsProductForStepFields as {
        currentPricingDetails?: {
          minimumAmountToBePaid?: number;
          minimumDownPaymentAmountToBePaid?: number;
          totalPrincipalAmountToBePaid?: number;
          totalAmountToBePaid?: number;
          amountToBePaid?: number;
          appliedNoOfMonths?: number;
        };
        appliedConfigId?: string;
      }
    ).currentPricingDetails;
    const minimumAmountToBePaid = Number(
      pricingDetails?.minimumAmountToBePaid ??
        pricingDetails?.minimumDownPaymentAmountToBePaid ??
        0
    );
    const totalAmountToBePaid = Number(
      pricingDetails?.totalPrincipalAmountToBePaid ??
        pricingDetails?.totalAmountToBePaid ??
        this.productFee
    );
    const defaultAmountToBePaid = Number(
      pricingDetails?.amountToBePaid ?? totalAmountToBePaid
    );

    this.enrollmentInstallmentMeta = {
      installmentType,
      minimumAmountToBePaid,
      totalAmountToBePaid,
      defaultAmountToBePaid,
      appliedNoOfMonths: Number(pricingDetails?.appliedNoOfMonths ?? 0),
      appliedConfigId: (
        paymentsProductForStepFields as { appliedConfigId?: string }
      ).appliedConfigId,
      ...externalManagedFields
    };
  }

  constructor(productData: any) {
    const {
      product_purchase_details_for_user: { applicable_discounts }
    } = productData;
    this.productCode = productData.product_code;
    this.productFee = resolveApplicationPortalProductFee(productData);
    this.productFeeAfterDiscount = this.productFee;
    this.termsAndConditions = productData.tnc_to_accept;
    this.extraDataConfig = JSON.parse(productData.extra_data);
    this.setApplicableDiscounts(applicable_discounts);

    let userExtraInfoConfig = JSON.parse(productData.user_extra_info_config);
    if (userExtraInfoConfig.form_type === 'default') {
      userExtraInfoConfig = transformUserExtraInfoConfig(userExtraInfoConfig);
    }

    let paymentsProductForStepFields: ReturnType<
      typeof createProductDetailModel
    > | null = null;
    try {
      paymentsProductForStepFields = createProductDetailModel(productData, null);
    } catch {
      paymentsProductForStepFields = null;
    }

    this.updateEnrollmentInstallmentMeta(paymentsProductForStepFields);

    if (
      userExtraInfoConfig &&
      userExtraInfoConfig.registration_form_details?.step_details?.length > 0
    ) {
      this.productFieldDetails = new StepConfig(
        userExtraInfoConfig.registration_form_details.step_details[0],
        null,
        paymentsProductForStepFields
      );
      this.productRequestObjectKeys =
        userExtraInfoConfig.request_object_extra_fields_data;

      this.productFieldDetails.stepFields.forEach((eachField) => {
        if (eachField.customYupValidation) {
          this.productValidationObject[eachField.field] =
            eachField.customYupValidation;
        }
      });
    }
    //NOTE: This is a temporary fix to get the success GA event from the extra data config, need to automate ga event
    if (this.extraDataConfig?.success_ga_event) {
      this.successGAEvent = this.extraDataConfig.success_ga_event;
    }
  }

  setProductFeeAfterDiscount(
    productFeeAfterDiscount: number | undefined,
    applicableDiscounts,
    productData?: any
  ) {
    this.productFeeAfterDiscount = productFeeAfterDiscount;
    if (productFeeAfterDiscount !== undefined) {
      this.setApplicableDiscounts(applicableDiscounts);
    }
    if (!productData) {
      return;
    }

    try {
      const paymentsProductForStepFields = createProductDetailModel(
        productData,
        null
      );
      this.updateEnrollmentInstallmentMeta(paymentsProductForStepFields);
    } catch {
      // Keep the previous installment meta when refreshed pricing cannot be parsed.
    }
  }

  setApplicableDiscounts(applicableDiscounts: any) {
    this.applicableDiscounts = applicableDiscounts;
    this.applicableDiscounts?.forEach((discount: any) => {
      if (discount.discount_type === 'COUPON') {
        this.setCouponDiscount(discount.discount_amount);
      } else if (discount.discount_type === 'REFERRAL') {
        this.setReferralDiscount(discount.discount_amount);
      }
    });
  }

  setReferralDiscount(referralDiscount: number | undefined) {
    this.referralDiscount = referralDiscount;
  }

  setCouponDiscount(couponDiscount: number | undefined) {
    this.couponDiscount = couponDiscount;
  }
}

export default ApplicationSectionProductModel;
